import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Button,
  ButtonColor,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  Checkbox,
  CheckboxState,
} from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { computeTreePhaseVoltage } from '@delmonicos/shared-sdk/libs/core/src/helpers/compute-phase-voltage';
import { displayedConnectorType } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/displayed-connector-type';
import {
  getCreateChargePointChannelDefaultValues,
  getCreateChargePointPhysicalSocketDefaultValues,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.form-default-values';
import { useCreateChargePointFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.form-validation';
import {
  CreateChargePointChannelModel,
  CreateChargePointModel,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.model';
import { CreatePhysicalSocketBaseModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.model';
import {
  ConnectorFormat,
  ConnectorType,
  PowerType,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  DC_DEFAULT_VOLTAGE,
  isDcConnector,
} from '@app/feature/charge-point/helper/connector-dc.helper';
import { PhysicalSocketDcPowerType } from '@app/feature/charge-point/physical-socket-dc-field/physical-socket-dc-power-type.component';
import { PhysicalSocketDcVoltage } from '@app/feature/charge-point/physical-socket-dc-field/physical-socket-dc-voltage.component';

import * as css from './charge-point-create-form-step-2.module.scss';

export interface ChargePointCreateFormStep2ChannelFragmentProps {
  index: number;
  onDeleteChannel: () => void;
}

export interface ChargePointCreateFormStep2PhysicalSocketFragmentProps {
  channelIndex: number;
  index: number;
  onDeletePhysicalSocket: () => void;
}

export const ChargePointCreateFormStep2PhysicalSocketFragment = ({
  channelIndex,
  index,
  onDeletePhysicalSocket,
}: ChargePointCreateFormStep2PhysicalSocketFragmentProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext<CreateChargePointModel>();

  const { format, amperage, powerType, standard, voltage } =
    useCreateChargePointFormValidation();

  // WATCHERS //////////////////////////////////////////////////////////////////

  const powerTypeValue = watch(
    `channels.${channelIndex}.physicalSockets.${index}.powerType`,
  );
  const amperageValue = watch(
    `channels.${channelIndex}.physicalSockets.${index}.amperage`,
  );
  const voltageValue = watch(
    `channels.${channelIndex}.physicalSockets.${index}.voltage`,
  );

  const standardValue = watch(
    `channels.${channelIndex}.physicalSockets.${index}.standard`,
  );
  const isDc = isDcConnector(standardValue);

  const handleStandardChange = (standardNew: ConnectorType) => {
    if (standardNew === ConnectorType.CHADEMO) {
      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.powerType`,
        PowerType.AC_3_PHASE,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.format`,
        ConnectorFormat.SOCKET,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.voltage`,
        400,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      return;
    }

    if (isDcConnector(standardNew)) {
      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.powerType`,
        PowerType.DC,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.format`,
        ConnectorFormat.CABLE,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.voltage`,
        DC_DEFAULT_VOLTAGE,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      return;
    }

    if (
      [
        ConnectorType.DOMESTIC_E,
        ConnectorType.DOMESTIC_F,
        ConnectorType.IEC_62196_T2,
      ].includes(standardNew)
    ) {
      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.powerType`,
        PowerType.AC_1_PHASE,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.format`,
        ConnectorFormat.SOCKET,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setValue(
        `channels.${channelIndex}.physicalSockets.${index}.voltage`,
        230,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      return;
    }

    setValue(
      `channels.${channelIndex}.physicalSockets.${index}.format`,
      ConnectorFormat.SOCKET,
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const realVoltageValue =
    powerTypeValue === PowerType.AC_3_PHASE
      ? computeTreePhaseVoltage(parseInt(`${voltageValue}`, 10))
      : voltageValue;

  return (
    <Card>
      <Stack direction="column" gap={GapEnum.L}>
        <Stack alignItems="center" justifyContent="space-between">
          <AccentTitle
            as={HeadingEnum.H2}
            icon="wire"
            text={t('$.charge-point-create.step-2.physical-sockets.title', {
              index: index + 1,
            })}
            variant={AccentVariantEnum.Surface}
          />
          <ButtonIcon
            color={ButtonColor.Ghost}
            icon="x-close"
            onClick={onDeletePhysicalSocket}
          />
        </Stack>
        <Controller
          name={`channels.${channelIndex}.physicalSockets.${index}.standard`}
          control={control}
          rules={standard}
          render={({ field, fieldState: { error } }) => (
            <Dropdown
              label={t(
                'delmonicos-shared-sdk.outputs-physical-socket.standard',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-physical-socket.standard',
              )}
              required
              removable
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(value) => {
                handleStandardChange(value as ConnectorType);
                field.onChange(value ?? '');
              }}>
              {displayedConnectorType.map((s) => (
                <Dropdown.Option key={s} value={s}>
                  {t(
                    `delmonicos-shared-sdk.ocpi-protocol-enums.connector-type.${s}`,
                  )}
                </Dropdown.Option>
              ))}
            </Dropdown>
          )}
        />
        <Stack gap={GapEnum.XS} direction="row">
          {isDc ? (
            <PhysicalSocketDcPowerType value={powerTypeValue} />
          ) : (
            <Controller
              name={`channels.${channelIndex}.physicalSockets.${index}.powerType`}
              control={control}
              rules={powerType}
              render={({ field, fieldState: { error } }) => (
                <Dropdown
                  label={t(
                    'delmonicos-shared-sdk.outputs-physical-socket.power-type',
                  )}
                  placeholder={t(
                    'delmonicos-shared-sdk.outputs-physical-socket.power-type',
                  )}
                  required
                  removable
                  errorMessage={error?.message}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value ?? '')}>
                  {Object.values(PowerType).map((s) => (
                    <Dropdown.Option key={s} value={s}>
                      {t(
                        `delmonicos-shared-sdk.ocpi-protocol-enums.power-type.${s}`,
                      )}
                    </Dropdown.Option>
                  ))}
                </Dropdown>
              )}
            />
          )}
          <Controller
            name={`channels.${channelIndex}.physicalSockets.${index}.voltage`}
            control={control}
            rules={voltage}
            render={({ field, fieldState: { error } }) => {
              return isDc ? (
                <PhysicalSocketDcVoltage
                  required={!!voltage.required}
                  errorMessage={error?.message}
                  value={field.value}
                  onChange={field.onChange}
                />
              ) : (
                <TextInput
                  label={t(
                    'delmonicos-shared-sdk.outputs-physical-socket.voltage',
                  )}
                  placeholder={t(
                    'delmonicos-shared-sdk.outputs-physical-socket.voltage',
                  )}
                  type="number"
                  required={!!voltage.required}
                  errorMessage={error?.message}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? '' : e.target.valueAsNumber,
                    )
                  }
                />
              );
            }}
          />
          <Controller
            name={`channels.${channelIndex}.physicalSockets.${index}.amperage`}
            control={control}
            rules={amperage}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextInput
                  label={t(
                    'delmonicos-shared-sdk.outputs-physical-socket.amperage',
                  )}
                  placeholder={t(
                    'delmonicos-shared-sdk.outputs-physical-socket.amperage',
                  )}
                  type="number"
                  required={!!amperage.required}
                  errorMessage={error?.message}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? '' : e.target.valueAsNumber,
                    )
                  }
                />
              );
            }}
          />
          <TextInput
            className={css.readOnly}
            readOnly
            label={t(
              '$.charge-point-create.step-2.physical-sockets.power-label',
            )}
            value={t('$.charge-point-create.step-2.physical-sockets.power', {
              power:
                powerType && amperageValue && voltageValue
                  ? ((amperageValue * realVoltageValue) / 1000).toFixed(0)
                  : '--',
            })}
          />
        </Stack>
        <Divider />
        <Controller
          name={`channels.${channelIndex}.physicalSockets.${index}.format`}
          control={control}
          rules={format}
          render={({ field }) => {
            return (
              <Checkbox
                state={isDc ? CheckboxState.Disabled : CheckboxState.Default}
                id={`channels.${channelIndex}.physicalSockets.${index}.format`}
                label={t(
                  `delmonicos-shared-sdk.ocpi-protocol-enums.connector-format-alt.${ConnectorFormat.CABLE}`,
                )}
                checked={field.value === ConnectorFormat.CABLE}
                onChange={(event) => {
                  field.onChange(
                    event.currentTarget.checked
                      ? ConnectorFormat.CABLE
                      : ConnectorFormat.SOCKET,
                  );
                }}
              />
            );
          }}
        />
      </Stack>
    </Card>
  );
};

export const ChargePointCreateFormStep2ChannelFragment = ({
  index,
  onDeleteChannel,
}: ChargePointCreateFormStep2ChannelFragmentProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateChargePointModel>();
  const { qrCode, physicalSockets } = useCreateChargePointFormValidation();

  // DYNAMIC FIELDS HANDLING ///////////////////////////////////////////////////

  const { append, fields, remove } = useFieldArray({
    control,
    name: `channels.${index}.physicalSockets`,
    rules: physicalSockets,
  });

  const handleRemovePhysicalSocket = (physicalSocketIndex: number) => {
    if (fields.length === 1) {
      toast.error(t('$.charge-point-create.step-2.physical-sockets.min-one'));
      return;
    }
    remove(physicalSocketIndex);
  };

  const handleAddPhysicalSocket = () => {
    append(
      getCreateChargePointPhysicalSocketDefaultValues() as CreatePhysicalSocketBaseModel,
    );
  };

  return (
    <Card>
      <Stack direction="column" gap={GapEnum.L}>
        <Stack alignItems="center" justifyContent="space-between">
          <AccentTitle
            as={HeadingEnum.H2}
            variant={AccentVariantEnum.Primary}
            icon="electric-terminal"
            text={t('$.charge-point-create.step-2.channels.title', {
              index: index + 1,
            })}
          />
          <ButtonIcon
            color={ButtonColor.Ghost}
            icon="x-close"
            onClick={onDeleteChannel}
          />
        </Stack>
        <Card>
          <Stack
            direction="row"
            gap={GapEnum.M}
            alignItems="center"
            justifyContent="stretch">
            <Controller
              name={`channels.${index}.qrCode`}
              control={control}
              rules={qrCode}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    className={css.stretch}
                    label={t('delmonicos-shared-sdk.outputs-channel.qr-code')}
                    placeholder={t(
                      'delmonicos-shared-sdk.outputs-channel.qr-code',
                    )}
                    type="string"
                    errorMessage={error?.message}
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.value,
                      )
                    }
                  />
                );
              }}
            />
          </Stack>
        </Card>
        <div className={css.physicalSocketsContainer}>
          {fields.map((channel, physicalSocketIndex) => (
            <ChargePointCreateFormStep2PhysicalSocketFragment
              key={channel.id}
              channelIndex={index}
              index={physicalSocketIndex}
              onDeletePhysicalSocket={() =>
                handleRemovePhysicalSocket(physicalSocketIndex)
              }
            />
          ))}
          <Stack alignItems="flex-start" flex={1}>
            <Button onClick={handleAddPhysicalSocket}>
              <Stack
                alignItems="center"
                justifyContent="center"
                gap={GapEnum.XS}>
                <Icon name="plus" />
                {t('$.charge-point-create.step-2.physical-sockets.add')}
              </Stack>
            </Button>
          </Stack>
        </div>
      </Stack>
    </Card>
  );
};

export const ChargePointCreateFormStep2Controller = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateChargePointModel>();
  const { channels } = useCreateChargePointFormValidation();

  // DYNAMIC FIELDS HANDLING ///////////////////////////////////////////////////

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'channels',
    rules: channels,
  });

  const handleRemoveChannel = (index: number) => {
    if (fields.length === 1) {
      toast.error(t('$.charge-point-create.step-2.channels.min-one'));
      return;
    }
    remove(index);
  };

  const handleAddChannel = () => {
    append(
      getCreateChargePointChannelDefaultValues() as CreateChargePointChannelModel,
    );
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      className={css.chargePointFormStep2}
      direction="column"
      gap={GapEnum.L}>
      <Stack
        direction="column"
        justifyContent="stretch"
        alignItems="stretch"
        flex={1}
        gap={GapEnum.L}>
        {fields.map((channel, index) => (
          <ChargePointCreateFormStep2ChannelFragment
            key={channel.id}
            index={index}
            onDeleteChannel={() => handleRemoveChannel(index)}
          />
        ))}
        <Stack alignItems="flex-start" flex={1}>
          <Button onClick={handleAddChannel}>
            <Stack alignItems="center" justifyContent="center" gap={GapEnum.XS}>
              <Icon name="plus" />
              {t('$.charge-point-create.step-2.channels.add')}
            </Stack>
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
